import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import useLocationQuery from '../hooks/useLocationQuery';
import LocationForm from '../components/LocationForm';
import getFormTitle from '#/common/utils/getFormTitle';
import useNotFoundGuard from '#/common/hooks/useNotFoundGuard';
import RecordNotFound from '#/app/pages/errors/RecordNotFound';

export interface LocationParams {
  readonly id?: string;
}

const LocationPage: React.FC = () => {
  const { id } = useParams() as LocationParams;
  const locationQuery = useLocationQuery(id);
  const location = locationQuery.data?.getLocation?.data;
  const isNotFound = useNotFoundGuard({ loading: locationQuery.loading, id, entity: location });

  // Use a ref to store the initial updatedAt value to prevent form remount
  // on background refetches (cache-and-network).
  const formKeyRef = useRef<string | null>(null);
  if (location && !formKeyRef.current) {
    formKeyRef.current = location.updatedAt ?? location.id ?? null;
  }

  const locationName = location?.name;
  const title = getFormTitle(id, locationName, 'Location');

  if (isNotFound) {
    return (
      <SubPage title={title} showGoBackButton type="form" isLoading={false}>
        <RecordNotFound backUrl="/assets/locations" />
      </SubPage>
    );
  }

  return (
    <SubPage title={title} isLoading={locationQuery.loading} showGoBackButton type="form">
      {
        <LocationForm
          key={`location-${id}-${formKeyRef.current}`}
          title={title}
          location={location || {}}
        />
      }
    </SubPage>
  );
};

export default React.memo(LocationPage);
